import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input, Modal, Select } from 'antd';
import { toast } from 'react-toastify';
import {
  getInventaireConfig, getInventaire, getCloture, getMouvementsFenetre,
  marquerAbsents, validerInventaire,
} from '../../services/inventaireService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeStatusBadge,
  NdoeStats, NdoeStatCard, NdoeError, NdoeLoading,
} from '../../components/ndoe';
import { money } from '../rapports/format';
import './inventaires.css';
import './inventaireCloture.css';

/*
  CLÔTURE D'UN INVENTAIRE — l'écran où l'on décide, pas celui où l'on compte.

  Un total brut (« 1 053 paires manquantes ») ne veut rien dire tant qu'on n'a
  pas séparé ce qui a des causes différentes. Tout cet écran sert à ce tri :

  1. JAMAIS COMPTÉ ≠ COMPTÉ À ZÉRO. Sur un inventaire total, des centaines de
     références n'ont été ouvertes par personne. La plupart sont déjà à zéro en
     stock : elles ne posent aucune question. Celles qui en posent une sont
     celles qui ont encore du stock en rayon — et pour celles-là, il faut un
     choix EXPLICITE : « on ne les a pas trouvées » (donc absentes) ou « on
     n'a pas fini » (donc on les laisse).

  2. ÉCART AVEC MOUVEMENT. La boutique vend pendant l'inventaire (décision du
     propriétaire). Un écart peut donc venir d'une vente survenue entre le
     comptage et maintenant. On met les deux faits côte à côte ; on ne déduit
     JAMAIS automatiquement — le rapprochement est un jugement humain.

  3. ÉCART SANS RIEN. C'est le vrai sujet, et il ne doit pas être noyé.

  Rien ici ne modifie le stock : la validation reste le seul geste qui écrit,
  et elle exige que chaque écart soit justifié.
*/

export default function InventaireCloture() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [header, setHeader] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [syn, setSyn] = useState(null);
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [absentsOuvert, setAbsentsOuvert] = useState(false);
  const [motif, setMotif] = useState(undefined);
  const [note, setNote] = useState('');
  const [marquage, setMarquage] = useState(false);
  const [validation, setValidation] = useState(false);
  const [validating, setValidating] = useState(false);
  const [voirMouvements, setVoirMouvements] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: h }, { data: c }, { data: s }] = await Promise.all([
        getInventaire(id), getInventaireConfig(), getCloture(id),
      ]);
      setHeader(h); setCfg(c); setSyn(s);
      const { data: m } = await getMouvementsFenetre(id).catch(() => ({ data: [] }));
      setMouvements(Array.isArray(m) ? m : []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de préparer la clôture.');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { charger(); }, [charger]);

  const classifications = useMemo(
    () => (cfg?.classifications || []).map((c) => ({ value: c.code, label: c.label })),
    [cfg],
  );

  const lancerMarquage = async () => {
    if (!motif) { toast.warn('Choisissez un motif : ces lignes deviendront des écarts justifiés.'); return; }
    if (motif === 'autre' && !note.trim()) { toast.warn('Le motif « Autre » demande un commentaire.'); return; }
    setMarquage(true);
    try {
      const { data } = await marquerAbsents(id, { classification: motif, commentaire: note.trim() || null });
      toast.success(`${data.lignes} référence(s) marquée(s) comme absentes.`);
      setAbsentsOuvert(false); setNote('');
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Marquage impossible.');
    } finally { setMarquage(false); }
  };

  const lancerValidation = async () => {
    setValidating(true);
    try {
      const { data } = await validerInventaire(id);
      if (data.statut === 'valide') {
        toast.success(`Inventaire validé : ${data.validees} correction(s), ${data.sans_ecart} ligne(s) juste(s).`);
      } else {
        toast.warn(`${data.validees} correction(s) — ${data.reste_a_recompter} ligne(s) à recompter (le stock a bougé pendant l'inventaire).`);
      }
      setValidation(false);
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Validation impossible.');
    } finally { setValidating(false); }
  };

  if (error && !loading) return <NdoePage><NdoeError text={error} onRetry={charger} /></NdoePage>;
  if (loading || !header || !syn) return <NdoePage><NdoeLoading rows={6} /></NdoePage>;

  const tri = syn.tri;
  const enCours = header.statut === 'en_cours';
  const peutValider = !!cfg?.peut_valider;
  const bloque = tri.non_justifies > 0;
  const restantARanger = tri.jamais_comptees_avec_stock;

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Stock · Inventaires"
        title={`Clôture — ${header.reference}`}
        subtitle="Trier les écarts par cause avant d'écrire quoi que ce soit dans le stock."
        actions={(
          <>
            <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}`)}>
              La session
            </NdoeActionButton>
            <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}/equipe`)}>
              Équipes
            </NdoeActionButton>
            {enCours && peutValider && (
              <NdoeActionButton onClick={() => setValidation(true)}>Valider l'inventaire</NdoeActionButton>
            )}
          </>
        )}
      />

      {!enCours && (
        <p className="pp-note">
          <NdoeStatusBadge kind="custom" tone={header.statut === 'valide' ? 'ok' : 'neutral'}
            label={header.statut === 'valide' ? 'Validé' : 'Abandonné'} />
          {' '}Session fermée. {header.statut === 'valide'
            && 'Les corrections sont tracées dans le journal (mouvements 14/15).'}
        </p>
      )}

      <NdoeStats className="pp-stats">
        <NdoeStatCard label="Manquants" value={tri.paires_manquantes} gold />
        <NdoeStatCard label="Surplus" value={tri.paires_surplus} />
        <NdoeStatCard
          label="Manquants (prix de vente)"
          value={Number(header.valeur_manquants_vente) ? money(header.valeur_manquants_vente) : '—'}
        />
        <NdoeStatCard
          label={header.cout_couverture_pct ? `Au coût (${header.cout_couverture_pct} %)` : 'Manquants au coût'}
          value={header.valeur_manquants_cout !== null && header.valeur_manquants_cout !== undefined
            ? money(header.valeur_manquants_cout) : '—'}
        />
      </NdoeStats>

      {/* --- Le tri ---------------------------------------------------------- */}
      <section className="ndoe-card cl-bloc">
        <h3 className="cl-titre">D’où viennent ces chiffres</h3>
        <div className="cl-tri">
          <Rangee
            libelle="Comptées et justes"
            valeur={`${tri.justes + tri.deja_sans_ecart} référence(s)`}
            ton="ok"
            aide="Le rayon correspond à ce qui est attendu. Rien à faire."
          />
          <Rangee
            libelle="Manquants constatés"
            valeur={`${tri.lignes_manquants} référence(s) · ${tri.paires_manquantes} paire(s)`}
            ton={tri.paires_manquantes ? 'danger' : 'neutre'}
            aide="Comptées, et il en manque. C’est ce qui sortira du stock à la validation."
          />
          <Rangee
            libelle="Surplus constatés"
            valeur={`${tri.lignes_surplus} référence(s) · ${tri.paires_surplus} paire(s)`}
            ton={tri.paires_surplus ? 'warn' : 'neutre'}
            aide="Plus de paires en rayon que prévu — souvent un retour ou une réception non enregistrés."
          />
          <Rangee
            libelle="Écarts explicables par un mouvement"
            valeur={`${tri.ecarts_avec_mouvement} référence(s)`}
            ton={tri.ecarts_avec_mouvement ? 'warn' : 'neutre'}
            aide="La boutique a vendu ces articles PENDANT l’inventaire. À rapprocher à la main : rien n’est déduit automatiquement."
          />
          <Rangee
            libelle="Jamais comptées, avec du stock en rayon"
            valeur={`${tri.jamais_comptees_avec_stock} référence(s) · ${tri.paires_jamais_comptees} paire(s)`}
            ton={restantARanger ? 'danger' : 'ok'}
            aide="Personne ne les a ouvertes. Tant qu’on n’a pas tranché, elles restent telles quelles — l’inventaire ne les corrige pas."
          />
          <Rangee
            libelle="Jamais comptées, déjà à zéro"
            valeur={`${tri.jamais_comptees - tri.jamais_comptees_avec_stock} référence(s)`}
            ton="neutre"
            aide="Sans enjeu : elles sont déjà à zéro en stock, les compter ne changerait rien."
          />
          {tri.a_recompter > 0 && (
            <Rangee
              libelle="À recompter"
              valeur={`${tri.a_recompter} référence(s)`}
              ton="warn"
              aide="Le stock a bougé entre le comptage et la validation. Rien n’a été écrasé : recomptez puis validez à nouveau."
            />
          )}
          {tri.non_justifies > 0 && (
            <Rangee
              libelle="Écarts sans justification"
              valeur={`${tri.non_justifies} référence(s)`}
              ton="danger"
              aide="Bloquant : classez chaque écart dans la session (filtre « Avec écart ») avant de valider."
            />
          )}
        </div>
      </section>

      {/* --- Les non comptées ------------------------------------------------- */}
      {enCours && peutValider && restantARanger > 0 && (
        <section className="ndoe-card cl-bloc cl-decision">
          <h3 className="cl-titre">Que fait-on des {restantARanger} références jamais comptées ?</h3>
          <p className="cl-texte">
            Elles totalisent <b className="num">{tri.paires_jamais_comptees}</b> paire(s) en rayon.
            Deux lectures possibles, et c’est vous qui tranchez :
          </p>
          <ul className="inv-recap">
            <li>
              <b>On n’a pas fini de compter</b> — laissez-les. La validation ne les touchera pas,
              leur stock reste inchangé. C’est le comportement par défaut.
            </li>
            <li>
              <b>On a tout parcouru et on ne les a pas trouvées</b> — marquez-les absentes. C’est
              ce qui fait qu’un inventaire <i>total</i> est total : ce qu’on n’a pas trouvé n’est
              pas là.
            </li>
          </ul>
          <p className="inv-aide">
            Le marquage enregistre un comptage de <b>zéro</b> pour chacune, comme si quelqu’un
            était passé devant l’étagère vide : c’est tracé à votre nom, annulable geste par
            geste, et soumis à la même justification que n’importe quel écart. Les références
            déjà à zéro ne sont pas touchées.
          </p>
          <div className="cl-actions">
            <NdoeActionButton variant="secondary" onClick={() => setAbsentsOuvert(true)}>
              Marquer les {restantARanger} références comme absentes
            </NdoeActionButton>
          </div>
        </section>
      )}

      {/* --- Écarts explicables ------------------------------------------------ */}
      {syn.suspects.length > 0 && (
        <section className="ndoe-card cl-bloc">
          <h3 className="cl-titre">Écarts survenus en même temps qu’un mouvement</h3>
          <p className="inv-aide">
            Ces articles ont bougé pendant l’inventaire. Cela n’explique pas forcément l’écart —
            c’est un rapprochement à faire, pas une conclusion.
          </p>
          <div className="cl-suspects">
            <div className="cl-suspect cl-suspect--head">
              <span>Article</span><span>Écart</span><span>Mouvements</span><span>Nature</span>
            </div>
            {syn.suspects.map((s) => (
              <div className="cl-suspect" key={s.id_varianteProduit}>
                <span className="cl-suspect__art">
                  <b>{s.nom_produit}</b>
                  <small>
                    {s.taille ? `P${s.taille}` : ''}
                    {s.couleur ? ` · ${s.couleur}` : ''}
                    {s.code_article ? ` · ${s.code_article}` : ''}
                    {s.en_circulation > 0 ? ` · ${s.en_circulation} en course` : ''}
                  </small>
                </span>
                <span className={`num ${s.ecart < 0 ? 'is-moins' : 'is-plus'}`}>
                  {s.ecart > 0 ? '+' : ''}{s.ecart}
                </span>
                <span className="num">{s.mouvements}</span>
                <span className="cl-suspect__types">{s.types || '—'}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* --- Les mouvements de la fenêtre --------------------------------------- */}
      {mouvements.length > 0 && (
        <section className="ndoe-card cl-bloc">
          <h3 className="cl-titre">
            Ce qui a bougé pendant l’inventaire
            <NdoeActionButton size="sm" variant="secondary" onClick={() => setVoirMouvements((v) => !v)}>
              {voirMouvements ? 'Masquer' : `Voir les ${mouvements.length}`}
            </NdoeActionButton>
          </h3>
          <p className="inv-aide">
            La boutique a continué de vendre : {mouvements.length} mouvement(s) depuis l’ouverture.
            Ils sont là pour l’enquête, jamais déduits des comptages.
          </p>
          {voirMouvements && (
            <div className="cl-mouvements">
              {mouvements.map((m, i) => (
                <div className="cl-mouvement" key={`${m.id_varianteProduit}-${i}`}>
                  <span className="cl-mouvement__quand num">{m.quand}</span>
                  <span>
                    <b>{m.nom_produit}</b>
                    {m.taille ? ` P${m.taille}` : ''}
                    {m.code_article ? ` · ${m.code_article}` : ''}
                  </span>
                  <span className="cl-mouvement__type">{m.type_mouvement || '—'}</span>
                  <span className="num">{m.quantite}</span>
                  <span className="cl-mouvement__qui">{m.auteur || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* --- Marquer les absents ------------------------------------------------ */}
      <Modal open={absentsOuvert} onCancel={() => setAbsentsOuvert(false)} footer={null}
        width={560} centered title="Marquer les références jamais comptées comme absentes">
        <div className="inv-form">
          <p>
            <b className="num">{restantARanger}</b> référence(s), soit
            {' '}<b className="num">{tri.paires_jamais_comptees}</b> paire(s) qui sortiront du stock
            à la validation.
          </p>
          <p className="inv-aide">
            Choisissez le motif qui sera porté sur ces écarts. Vous pourrez le corriger
            référence par référence ensuite, ou annuler le marquage geste par geste.
          </p>
          <Select
            placeholder="Motif de l’écart"
            options={classifications}
            value={motif}
            onChange={setMotif}
            style={{ width: '100%' }}
          />
          <Input
            placeholder={motif === 'autre' ? 'Précisez (obligatoire pour « Autre »)' : 'Commentaire (optionnel)'}
            value={note}
            maxLength={255}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="inv-form__actions">
            <NdoeActionButton variant="secondary" onClick={() => setAbsentsOuvert(false)}>Retour</NdoeActionButton>
            <NdoeActionButton loading={marquage} onClick={lancerMarquage}>
              Marquer {restantARanger} référence(s)
            </NdoeActionButton>
          </div>
        </div>
      </Modal>

      {/* --- Valider -------------------------------------------------------------- */}
      <Modal open={validation} onCancel={() => setValidation(false)} footer={null}
        width={560} centered title="Valider l'inventaire">
        <div className="inv-form">
          <p>
            <b className="is-moins">{tri.paires_manquantes} paire(s) manquante(s)</b> et
            {' '}<b className="is-plus">{tri.paires_surplus} en surplus</b> sur
            {' '}{tri.lignes_manquants + tri.lignes_surplus} référence(s).
          </p>
          <ul className="inv-recap">
            <li>Chaque écart devient un mouvement d’ajustement tracé + une correction justifiée.</li>
            <li>Le stock est recalé à « compté + paires en course » : les livraisons en cours ne sont pas perdues.</li>
            <li>Si un article a bougé depuis son comptage, il repasse « à recompter » — rien n’est écrasé.</li>
            {restantARanger > 0 && (
              <li>
                <b>{restantARanger} référence(s) jamais comptées</b> ({tri.paires_jamais_comptees} paires)
                resteront telles quelles : la validation ne les touchera pas.
              </li>
            )}
          </ul>
          {bloque && (
            <p className="inv-bloquant">
              ⛔ {tri.non_justifies} écart(s) sans justification. Classez-les dans la session
              (filtre « Avec écart ») avant de valider.
            </p>
          )}
          <div className="inv-form__actions">
            <NdoeActionButton variant="secondary" onClick={() => setValidation(false)}>Retour</NdoeActionButton>
            <NdoeActionButton loading={validating} disabled={bloque} onClick={lancerValidation}>
              Appliquer les corrections
            </NdoeActionButton>
          </div>
        </div>
      </Modal>
    </NdoePage>
  );
}

// Une ligne du tri : l'intitulé, le chiffre, et SURTOUT ce que ça veut dire.
// Sans la phrase d'explication, « 799 références jamais comptées » se lit comme
// une alarme alors que c'est le cas normal d'un inventaire total.
function Rangee({ libelle, valeur, ton, aide }) {
  return (
    <div className={`cl-rangee cl-rangee--${ton}`}>
      <span className="cl-rangee__lib">{libelle}</span>
      <span className="cl-rangee__val num">{valeur}</span>
      <small className="cl-rangee__aide">{aide}</small>
    </div>
  );
}
